import { Component, DestroyRef, inject, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators, } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { HttpErrorResponse } from '@angular/common/http';
import { MOBILE_TOOLTIP_MSG, USER_ROLES } from '@shared/constant/shared.constant'; 
import { ProfileService } from '@core/services/profile.service';
import { AuthService } from '@core/services/auth.service';
import { CustomValidator, emailOrPhoneValidator } from '@shared/services/custom-validatior.abstract';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { ResetPasswordComponent } from '@shared/components/reset-password/reset-password.component';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { MenuService } from '@core/services/menu.service';
import { ToastService } from '@shared/services/toast.service';
import { LoginService } from '@auth/services/login.service';
import { LoginResponse } from '@auth/models/login-response.model';
import { LoginRequest } from '@auth/models/login-request.model';
import { CommonSingupPopupComponent } from '@auth/common-singup-popup/common-singup-popup.component';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  loginForm!: FormGroup;
  hasPermission:boolean = false;
  previousUrl:string = '';
  userRoles = USER_ROLES;
  mobileMsg = MOBILE_TOOLTIP_MSG;
  constructor(
    private readonly profileService: ProfileService,
    private readonly authService: AuthService,
    private readonly fb: FormBuilder,
    private readonly dialogControlService: DialogControlService,
    private readonly router: Router, private readonly loginService: LoginService, private readonly cookieService: CookieService,
    private readonly menuService:MenuService,
    private readonly toastService: ToastService,
    private readonly renderer: Renderer2,
    private readonly encryptStorage: EncryptionStorageService
  ) {
  }

  ngOnInit(): void {
    this.router.events.subscribe(() => {
      this.renderer.removeClass(document.body, 'logged-in');
    });
    this.previousUrl = this.encryptStorage.getItem('previousUrl')
    localStorage.clear();
    this.dialogControlService.closeDialog();
    const rememberedUser = this.cookieService.get('rememberedUser');
    const email = this.cookieService.get('rememberedUsername');
    const password = this.cookieService.get('rememberedPassword')

    this.loginForm = this.fb.group({
      email: [rememberedUser ? email : "", [Validators.required, emailOrPhoneValidator]],
      password: [rememberedUser ? password : "", [Validators.required]],
      rememberMe: [rememberedUser ? true : false],
      deviceType: 3
    })
  }

  login(): void {
    if (this.loginForm.valid) {
      const apiRequest: LoginRequest = { ...this.loginForm.value };
      this.loginService.loginUser(apiRequest).pipe(
        autoUnsubWith(this.destroyRef))
        .subscribe({
          next: (loginResponse: LoginResponse) => {
            if(loginResponse.isForcePasswordUpdate){
               this.authService.setToken(loginResponse.token);
              this.router.navigateByUrl('change-password');
            }else{
              this.checkIsEmailOrNumber(loginResponse, this.loginForm.value);
            }
       
            if(loginResponse.roleName !== USER_ROLES.SUPER_ADMIN ){
              this.encryptStorage.setItem('companyId',loginResponse.companyId)
              }
          },
          error: (error: HttpErrorResponse) => {
            this.handleLoginError(error);
          }
        });

      this.handleRememberMe();
    } else {
      CustomValidator.markFormGroupTouched(this.loginForm);
    }
  }
  private checkIsEmailOrNumber(loginResponse: LoginResponse, value: any): void {
    const isEmail = value.email.includes('@');
    this.handleLoginResponse(loginResponse, isEmail);
  }
  private handleLoginResponse(loginResponse: LoginResponse, isEmail): void {
    const { roleName, isEmailVerified, token, id, name, profileImagePath } = loginResponse;
    const profileData: any = { name, img: profileImagePath };

    if ((roleName === this.userRoles.DRIVER || roleName === this.userRoles.GROUP_USER) && isEmailVerified !== true) {
      this.handleUnverifiedEmail(loginResponse, roleName, isEmail);
    } else {
      this.authService.setToken(token);
      this.authService.setUserId(id);
      this.encryptStorage.setItem('roleName', roleName);
      this.profileService.setProfileData(profileData);
      this.toastService.showToastMessage('success', 'Success', 'Login Successful');
      this.menuService.getRoleBaseMenuAndActionPermission()
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (menuResponse : any) => {
          this.hasPermission = this.menuService.shortNameExists(menuResponse, 24);
          this.navigateToDashboard(roleName);
      }
    });
    }
  }

  private handleUnverifiedEmail(res: LoginResponse,roleName, isEmail): void {
    this.encryptStorage.setItem('isEmailVerified', 'false');
    this.encryptStorage.setItem('email', res.email);
    const userData = {
      email: isEmail ? res.email : null,
      mobile: isEmail ? null : res.mobileNumber
    };
    this.encryptStorage.setItem('loginInput', JSON.stringify(userData));

    this.toastService.showToastMessage('info','Info','Please verify your account');
    setTimeout(() => {
      if(roleName === this.userRoles.DRIVER){
        this.router.navigateByUrl('driver/registration');
      }
      if(roleName === this.userRoles.GROUP_USER){
        this.router.navigateByUrl('group-operator/registration');

      }
    }, 1000);
  }

  private navigateToDashboard(roleName: string): void {
    if (roleName === this.userRoles.DRIVER) {
      this.router.navigate(['driver/create-booking']);
    } else if (roleName === this.userRoles.SUPER_ADMIN) {
      this.router.navigate(['super-admin/dashboard']);
    } else if(roleName === this.userRoles.GROUP_USER){
      this.router.navigate(['group-operator/create-booking']);
    } else {
      const targetUrl = this.hasPermission ? 'manager/dashboard' : 'manager/user/profile';
      this.router.navigate([targetUrl]);    }
  }

  private handleLoginError(error: HttpErrorResponse): void {
    this.toastService.showToastMessage( 'error','Error', error.error.message );
  }

  private handleRememberMe(): void {
    if (this.loginForm.value.rememberMe) {
      this.cookieService.set('rememberedUser', 'true', 30, null, null, true);
      this.cookieService.set('rememberedUsername', this.loginForm.value.email, 30, null, null, true);
    } else {
      this.cookieService.delete('rememberedUser');
      this.cookieService.delete('rememberedUsername');
    }
  }

redirectToLanding(): void {
    if(!this.checkPreviousUrl()){
      Utility.setMoveToEnquiryElement();
      this.dialogControlService.openDialog(CommonSingupPopupComponent, {
        styleClass: 'login-model',
      });
    }
  }

  checkPreviousUrl(): boolean {
    switch (this.previousUrl) {
      case '/parking-lot-manager':
      case '/onboarding':
        this.router.navigate(['/onboarding']);
        return true;

      case '/truck-driver':
      case '/driver/registration':
        this.router.navigate(['/driver/registration']);
        return true;

      case '/group-operator':
      case '/group-operator/registration':
        this.router.navigate(['/group-operator/registration']);
        return true;

      default:
        return false;
    }
  }

resetPassword(){
    this.dialogControlService.openDialog(ResetPasswordComponent, {
      width: '33%',
    });
  }

}
